import { gql } from '@apollo/client';
import { Typography, useTheme } from '@mui/material';
import { Image } from '@realadvisor/image';
import { HouseIcon } from 'lucide-react';

import type { ListingCell_ListingFragment } from '@graphql/__generated__/graphql';
import { formatAddress } from '@modules/_shared/utils/formatting';

export const LISTING_CELL_LISTING_FRAGMENT = gql`
  fragment ListingCell_listing on lots {
    id
    title
    lot_reference
    property {
      id
      route
      street_number
      postcode
      locality
      country_code
      single_property_images: property_images(
        order_by: { order_nr: asc }
        limit: 1
      ) {
        id
        order_nr
        image {
          id
          url
        }
      }
    }
  }
`;

interface ListingCellProps {
  listing?: ListingCell_ListingFragment;
  showLotReference?: boolean;
}

export const ListingCell = ({
  listing,
  showLotReference = false,
}: ListingCellProps) => {
  const theme = useTheme();
  const { text } = theme;
  // Prefer CSS-variable palette so colors follow the active color scheme.
  const palette = (theme.vars ?? theme).palette;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'max-content 1fr',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <div style={{ position: 'relative' }}>
        <div
          style={{
            height: 50,
            width: 50,
            backgroundColor: palette.grey['200'],
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {listing?.property?.single_property_images &&
          listing?.property?.single_property_images.length > 0 ? (
            <Image
              src={
                listing?.property?.single_property_images[0]?.image.url ?? ''
              }
              options={{ w: 256, f: 'jpg' }}
            />
          ) : (
            <HouseIcon size={32} color={'var(--mui-palette-grey-400)'} />
          )}
        </div>
      </div>
      <div style={{ minWidth: 0 }}>
        <Typography variant="body2" sx={text.ellipsis}>
          {listing?.title}
        </Typography>
        {listing != null &&
          (showLotReference ? (
            <Typography
              variant="caption"
              sx={[{ color: palette.text.secondary }, text.ellipsis]}
            >
              {listing.lot_reference ?? '-'}
            </Typography>
          ) : (
            formatAddress(listing?.property, null).map((line, index) => (
              <div
                key={index}
                style={{
                  ...text.caption,
                  ...text.ellipsis,
                  color: palette.text.secondary,
                }}
              >
                {line}
              </div>
            ))
          ))}
      </div>
    </div>
  );
};
